const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

/**
 * Get authentication headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }
    
    console.error('API Error Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      errorData
    });
    
    const errorMessage = errorData?.message || errorData?.details || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Creates a new database backup
 * @param {string} backupName - Optional name for the backup
 * @returns {Promise<Object>} Backup creation result
 */
export const createBackup = async (backupName = null) => {
  try {
    const requestBody = backupName ? { BackupName: backupName } : {};
    const response = await fetch(`${API_BASE_URL}/api/backup/create-backup`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error creating backup:', error);
    throw error;
  }
};

/**
 * Lists all available backup files
 * @returns {Promise<Object>} List of backup files with metadata
 */
export const listBackups = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/backup/list-backups`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error listing backups:', error);
    throw error;
  }
};

/**
 * Deletes a specific backup file
 * @param {string} fileName - Name of the backup file to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteBackup = async (fileName) => {
  try {
    if (!fileName) {
      throw new Error('File name is required');
    }

    const response = await fetch(`${API_BASE_URL}/api/backup/delete-backup/${encodeURIComponent(fileName)}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error deleting backup:', error);
    throw error;
  }
};

/**
 * Cleans up old backup files based on age criteria
 * @param {number} daysOld - Delete backups older than this many days (default: 30)
 * @param {number} keepMinimum - Always keep at least this many backups (default: 5)
 * @returns {Promise<Object>} Cleanup result
 */
export const cleanupOldBackups = async (daysOld = 30, keepMinimum = 5) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/backup/cleanup-old-backups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ 
        DaysOld: daysOld, 
        KeepMinimum: keepMinimum 
      })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error cleaning up old backups:', error);
    throw error;
  }
};

/**
 * Restores database from a backup file
 * @param {string} fileName - Name of the backup file to restore from
 * @returns {Promise<Object>} Restore result
 */
export const restoreBackup = async (fileName) => {
  try {
    if (!fileName) {
      throw new Error('File name is required');
    }

    const response = await fetch(`${API_BASE_URL}/api/backup/restore-backup`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ FileName: fileName })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error restoring backup:', error);
    throw error;
  }
};

/**
 * Gets backup directory status and information
 * @returns {Promise<Object>} Backup status information
 */
export const getBackupStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/backup/backup-status`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting backup status:', error);
    throw error;
  }
};

/**
 * Cleans up backups to keep only the last 14 days
 * @returns {Promise<Object>} Cleanup result
 */
export const cleanupTo14Days = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/backup/cleanup-to-14-days`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error cleaning up to 14 days:', error);
    throw error;
  }
};

/**
 * Formats file size in MB to human readable format
 * @param {number} sizeMB - Size in megabytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = (sizeMB) => {
  if (sizeMB < 1) {
    return `${Math.round(sizeMB * 1024)} KB`;
  } else if (sizeMB < 1024) {
    return `${Math.round(sizeMB * 10) / 10} MB`;
  } else {
    return `${Math.round((sizeMB / 1024) * 10) / 10} GB`;
  }
};

/**
 * Formats age in days to human readable format
 * @param {number} ageInDays - Age in days
 * @returns {string} Formatted age string
 */
export const formatAge = (ageInDays) => {
  if (ageInDays < 1) {
    const hours = Math.round(ageInDays * 24);
    return `${hours} שעות`;
  } else if (ageInDays < 7) {
    return `${Math.round(ageInDays * 10) / 10} ימים`;
  } else if (ageInDays < 30) {
    const weeks = Math.round(ageInDays / 7);
    return `${weeks} שבועות`;
  } else {
    const months = Math.round(ageInDays / 30);
    return `${months} חודשים`;
  }
};

/**
 * Formats date to Hebrew locale
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'תאריך לא תקין';
  }
};